// EntityCommentsDrawer — the reusable host/crew comments panel (roadmap #6). Any entity admin page (POI,
// accommodation, signup item, budget item, travel leg, task, or the event guest-book) mounts this to read
// the thread and post as host/crew. A comment can be marked "Crew only" (guests never see it). Host sees
// BOTH guest and crew comments; the crew-only ones carry a badge. Account scoping + crew-only visibility are
// enforced authoritatively server-side (CommentCore / PublicCommentCore) — this is presentation + calls.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ActionIcon, Avatar, Badge, Button, Collapse, Group, Loader, Paper, Stack, Switch, Text, Textarea, Tooltip } from "@mantine/core";
import { OP_RESULT_CODES, type OpResult } from "@sdflc/api-helpers";

import { useCommentOutbox, CommentBody, CommentComposerHelp, isHelpCommand, type OutboxComment } from "@em/blocks";
import { appendOlderPage, olderCursor } from "@em/utils";

import { AppDrawer, AppIcon, ConfirmModal, DiscardGuard, LoadMoreButton, ReactionMenu } from "@/components/generic";
import { appEnv } from "@/config/branding";
import { useApp } from "@/contexts/AppContext/AppContext";
import { formatErrors, getFormErrors, refusalReason } from "@/utils";
import { CommentService, commentAuthorPicture, type EventComment, type EventCommentReaction, listAllPages } from "@/utils/api";
import { openTicketedStream } from "@/utils/ticketedStream";

const PAGE_SIZE = 20;

/**
 * The one field name the comment server names in a refusal (CommentCore post/edit:
 * "Please write a comment."). ⚠ Each of the three composers below reads
 * `<x>FieldErrors.commentText` onto its own textarea — a name listed here and not read
 * would land in a map nothing renders and the refusal would disappear.
 */
const COMMENT_FORM_FIELDS = ["commentText"] as const;

const fmtWhen = (iso?: string | null): string => {
  if (!iso) {
    return "";
  }
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return "";
  }
};

const errText = (r: OpResult): string =>
  refusalReason(r) || "Something went wrong. Please try again.";

// Pure optimistic toggle of one emoji on a comment's reaction summary — its own inverse.
const toggleReactionSummary = (reactions: EventCommentReaction[], emoji: string): EventCommentReaction[] => {
  const arr = (reactions || []).map((r) => ({ ...r }));
  const idx = arr.findIndex((r) => r.reaction === emoji);
  if (idx < 0) {
    arr.push({ reaction: emoji, count: 1, mine: true });
    return arr;
  }
  const r = arr[idx];
  if (r.mine) {
    const count = (r.count || 1) - 1;
    if (count <= 0) {
      arr.splice(idx, 1);
    } else {
      arr[idx] = { ...r, count, mine: false };
    }
  } else {
    arr[idx] = { ...r, count: (r.count || 0) + 1, mine: true };
  }
  return arr;
};

// Apply an authoritative viewer-agnostic tally (from a live reaction.updated) onto the local summary, keeping
// our own "mine" flags (broadcasts carry counts only).
const mergeReactionTallies = (
  local: EventCommentReaction[],
  tallies: { reaction: string; count: number }[]
): EventCommentReaction[] => {
  const mine = new Set((local || []).filter((r) => r.mine).map((r) => r.reaction));
  return (tallies || [])
    .filter((t) => (t.count || 0) > 0)
    .map((t) => ({ reaction: t.reaction, count: t.count, mine: mine.has(t.reaction) }));
};

export interface EntityCommentsDrawerProps {
  opened: boolean;
  onClose: () => void;
  eventId: string;
  /** guestbook | poi | accommodation | signup | budget | travel | task. */
  entityKind: string;
  /** The specific item's id (for the guest-book pass the event id or omit). */
  entityId?: string | null;
  /** Item name for the drawer title. */
  title?: string;
}

export function EntityCommentsDrawer({ opened, onClose, eventId, entityKind, entityId = null, title }: EntityCommentsDrawerProps) {
  const { user } = useApp();
  const hostName = [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim() || user?.username || "Host";

  const [channelId, setChannelId] = useState<string | null>(null);
  const [comments, setComments] = useState<EventComment[]>([]);
  // #128 — the comments on screen, for Load more's cursor and merge (read when the answer lands, not when it was asked).
  const commentsRef = useRef<EventComment[]>([]);
  commentsRef.current = comments;
  const [loading, setLoading] = useState(false);
  /**
   * ⚠ Whether the FIRST read for this thread has COME BACK — not the same as "not loading". The
   * open effect runs after the first paint, so branching on `loading && comments.length === 0`
   * showed "No comments yet." for one frame every time the drawer opened.
   */
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  /**
   * ⚠ Bumped by every RELOAD of the thread — opening it on a record, a live update, a reconnect, a reply. A page that
   * lands after a newer reload began is dropped: it may be ANOTHER record's thread, and it used to be appended to this
   * one (#119). A Load more captures the id without bumping it, so it is dropped too when a reload overtakes it.
   */
  const loadIdRef = useRef(0);
  // Thread-level failures with no composer of their own (load, remove, a reaction toggle).
  const [error, setError] = useState<string | null>(null);
  // Each composer keeps the OpResult of ITS OWN call, so the refusal can be drawn on the
  // box the host actually typed in.
  const [postResult, setPostResult] = useState<OpResult | null>(null);
  const [replyResult, setReplyResult] = useState<OpResult | null>(null);
  const [editResult, setEditResult] = useState<OpResult | null>(null);

  const [text, setText] = useState("");
  const [crewOnly, setCrewOnly] = useState(false);
  // The "Formatting & commands" help popover (also opened by typing `/help`).
  const [helpOpen, setHelpOpen] = useState(false);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  // Who's currently typing in this thread (senderId/visitorId → { name, last-seen ts }); auto-expires. The host
  // drawer now SHOWS typing (guests + other crew), like the guest book — its own pings are filtered out.
  const [typing, setTyping] = useState<Record<string, { name: string; at: number }>>({});

  // Insert a picked command/snippet into the composer (append with a single separating space), then refocus.
  const insertIntoComposer = useCallback((snippet: string) => {
    setText((prev) => {
      const sep = !prev || /\s$/.test(prev) ? "" : " ";
      return `${prev}${sep}${snippet}`;
    });
    requestAnimationFrame(() => composerRef.current?.focus());
  }, []);

  const [repliesByParent, setRepliesByParent] = useState<Record<string, EventComment[]>>({});
  const [openReplies, setOpenReplies] = useState<Record<string, boolean>>({});
  const [replyFor, setReplyFor] = useState<string | null>(null);
  const [replyText, setReplyText] = useState("");
  const [replyCrewOnly, setReplyCrewOnly] = useState(false);
  const [replyPosting, setReplyPosting] = useState(false);

  // Inline edit of one's own comment (only while it's the newest in the thread — server-enforced).
  const [editFor, setEditFor] = useState<string | null>(null);
  const [editText, setEditText] = useState("");
  // The comment's text when the edit began — an edit is unsaved work only once it differs.
  const [editOriginal, setEditOriginal] = useState("");
  const [editPosting, setEditPosting] = useState(false);

  const [removeId, setRemoveId] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);

  // Load top-level comments (both visibilities — host sees everything) for this entity's channel.
  const load = useCallback(
    // `append`: Load more — the page OLDER than the oldest comment shown, by that comment (#128/#133: a cursor, because a
    // comment posted meanwhile shifts a page number by one and repeats or skips one). Otherwise the first page.
    async (chId: string, append: boolean) => {
      const cursor = append ? olderCursor(commentsRef.current) : null;

      if (append && !cursor) {
        return;
      }

      const callId = append ? loadIdRef.current : ++loadIdRef.current;

      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        // A reload owns the list: a Load more it supersedes is dropped, and so is its spinner (#114).
        setLoadingMore(false);
        setError(null);
      }

      // Only scope to a channel when we actually have one — `chId` can arrive as the
      // string "undefined" before the channel is ensured, which the backend can't parse.
      const hasChannel = !!chId && chId !== "undefined" && chId !== "null";
      const res = await CommentService.list(
        { eventId: [eventId], ...(hasChannel ? { channelId: [chId] } : {}), channelCommentId: null, ...(cursor ?? {}) },
        { page: 1, pageSize: PAGE_SIZE }
      );

      // ⚠ A stale read touches nothing — not the rows, and not the flags: the newer read owns those (#119).
      if (callId !== loadIdRef.current) {
        return;
      }

      if (res.code === OP_RESULT_CODES.OK) {
        const rows = (Array.isArray(res.data) ? (res.data as EventComment[]) : []).filter((c) => !!c.id);
        if (append) {
          setHasMore(appendOlderPage(commentsRef.current, rows, PAGE_SIZE).hasMore);
          setComments((prev) => appendOlderPage(prev, rows, PAGE_SIZE).items);
        } else {
          setComments(rows);
          setHasMore(rows.length >= PAGE_SIZE);
        }
      } else if (!append) {
        setError(errText(res));
      }

      setLoading(false);
      setLoadingMore(false);
      // ⚠ On every path — a read that FAILED has still come back, and leaving the flag false would
      // hold the spinner on screen for ever instead of showing what went wrong.
      setIsLoaded(true);
    },
    [eventId]
  );

  // ⚠ Refuses while the thread RELOADS (#129): pressed then, it would capture the reload's id and append a page older
  // than the OLD rows — possibly another record's — to the new ones. The button hides itself while `loading`; this
  // does not rely on that.
  const loadMore = useCallback(() => {
    if (loading || loadingMore || !hasMore || !channelId) {
      return;
    }

    void load(channelId, true);
  }, [loading, loadingMore, hasMore, channelId, load]);

  // On open, ensure the entity's channel exists (idempotent — a container for the thread) then load it.
  useEffect(() => {
    if (!opened) {
      return;
    }
    let cancelled = false;
    // ⚠ A new thread: any read still out for the previous one (a Load more, a live reload) is now stale, and so is
    // its spinner (#119).
    ++loadIdRef.current;
    setComments([]);
    setRepliesByParent({});
    setOpenReplies({});
    setChannelId(null);
    setLoading(true);
    setLoadingMore(false);
    setIsLoaded(false);
    void (async () => {
      const ensured = await CommentService.ensureChannel(eventId, entityKind, entityId ?? undefined);
      const ch = ensured.code === OP_RESULT_CODES.OK && Array.isArray(ensured.data) ? (ensured.data[0] as { id?: string }) : null;
      if (cancelled) {
        return;
      }
      if (!ch?.id) {
        setError(errText(ensured));
        setLoading(false);
        // ⚠ The channel could not be ensured, so no list read will follow — release the spinner
        // or the drawer sits on it for ever with the error underneath it.
        setIsLoaded(true);
        return;
      }
      setChannelId(ch.id);
      await load(ch.id, false);
    })();
    return () => {
      cancelled = true;
    };
  }, [opened, eventId, entityKind, entityId, load]);

  // Real-time (roadmap #6b): while the drawer is open, subscribe to the event's AUTHED comment stream. A
  // short-lived ticket (minted after an ownership check) authorizes it, so it includes crew-only comments and
  // works on draft events. It's event-scoped (one connection), so we filter to THIS entity's thread. New
  // comments/removals appear live; on reconnect we refetch (the crew stream doesn't replay). Degrades
  // gracefully: no ticket / no EventSource just means no live push — the drawer still works via refresh.
  const channelIdRef = useRef(channelId);
  channelIdRef.current = channelId;
  // The active stream ticket (also used to authorize host "is typing" pings) + this host's sender id + the
  // last-sent throttle. senderId keeps distinct crew members apart in others' typing indicators.
  const ticketRef = useRef<string | null>(null);
  const senderId = `host:${user?.username || "host"}`;
  const lastTypingSentRef = useRef(0);
  // Bridge to the optimistic outbox (defined below the stream effect): when the SSE echoes OUR OWN just-posted
  // comment, we reconcile it against the matching pending draft IN THE SAME tick, so the thread never briefly
  // shows both the "sending" row and the real comment. Refs so the effect always reads the latest.
  const outboxPendingRef = useRef<OutboxComment[]>([]);
  const outboxDiscardRef = useRef<(tempId: string) => void>(() => {});
  // Bridge to updateCommentReactions (defined below) so the stream handler can apply live reaction.updated.
  const updateReactionsRef = useRef<(commentId: string, updater: (r: EventCommentReaction[]) => EventCommentReaction[]) => void>(
    () => {}
  );
  useEffect(() => {
    if (!opened || !eventId || typeof EventSource === "undefined") {
      return;
    }
    const base = (appEnv.rootApiUrl || "").replace(/\/$/, "");
    if (!base) {
      return;
    }

    const myEntityId = entityId ?? null;

    const matchesThread = (ev: { entityKind?: string | null; entityId?: string | null }): boolean =>
      (ev.entityKind ?? "") === entityKind && String(ev.entityId ?? "") === String(myEntityId ?? "");

    const reload = () => {
      if (channelIdRef.current) {
        void load(channelIdRef.current, false);
      }
    };

    // Every frame of the ticketed host/crew stream (a parsed data frame; `ticket.invalid` is handled by the opener).
    const onFrame = (msg: any): void => {
      // The host drawer surfaces comments + removals + reactions + typing; `hello` is the reconnect baseline.
      if (!msg?.type || msg.type === "hello") {
        return;
      }

      // "X is typing…" in THIS thread (a guest or another crew member) — record it; it auto-expires. Our own
      // host pings (senderId) are filtered out. Crew-only typing reaches only crew streams (this is one).
      if (msg.type === "typing") {
        const who = msg.visitorId;
        if (who && who !== senderId && msg.name && matchesThread(msg)) {
          setTyping((prev) => ({ ...prev, [who]: { name: String(msg.name), at: Date.now() } }));
        }
        return;
      }

      // A comment's reactions changed elsewhere (a guest or another crew member) — merge the authoritative
      // tallies, keeping our own "mine". Matches by commentId across whichever thread holds it.
      if (msg.type === "reaction.updated") {
        const cid = msg.commentId;
        if (cid) {
          const tallies = Array.isArray(msg.reactions) ? msg.reactions : [];
          updateReactionsRef.current(cid, (rs) => mergeReactionTallies(rs, tallies));
        }
        return;
      }

      if (msg.type === "resync") {
        reload();
        return;
      }

      // A comment's TEXT was edited by its author — patch it live wherever it's shown.
      if (msg.type === "comment.updated") {
        const uc = msg.comment as { id?: string; commentText?: string } | undefined;
        if (!uc?.id) {
          return;
        }
        const patch = (c: EventComment): EventComment => (c.id === uc.id ? { ...c, commentText: uc.commentText ?? c.commentText } : c);
        setComments((prev) => prev.map(patch));
        setRepliesByParent((prev) => {
          const next: Record<string, EventComment[]> = {};
          for (const [k, list] of Object.entries(prev)) {
            next[k] = list.map(patch);
          }
          return next;
        });
        return;
      }

      if (msg.type === "comment.removed") {
        const cid = msg.commentId;
        if (!cid) {
          return;
        }
        setComments((prev) => prev.filter((c) => c.id !== cid));
        setRepliesByParent((prev) => {
          const next: Record<string, EventComment[]> = {};
          for (const [k, list] of Object.entries(prev)) {
            if (k === cid) {
              continue; // the removed comment was a parent — drop its whole replies bucket
            }
            next[k] = list.filter((c) => c.id !== cid);
          }
          return next;
        });
        return;
      }

      if (msg.type === "comment.created" && msg.comment?.id && matchesThread(msg)) {
        const incoming = msg.comment as EventComment;
        // Their message landed → they've stopped typing; drop that person's indicator.
        setTyping((prev) => {
          const next = { ...prev };
          let changed = false;
          for (const [who, v] of Object.entries(prev)) {
            if (v.name === incoming.commenterName) {
              delete next[who];
              changed = true;
            }
          }
          return changed ? next : prev;
        });
        if (incoming.channelCommentId) {
          const parentId = incoming.channelCommentId;
          setComments((prev) => prev.map((c) => (c.id === parentId ? { ...c, hasReplies: true } : c)));
          setRepliesByParent((prev) => {
            const list = prev[parentId];
            if (!list || list.some((r) => r.id === incoming.id)) {
              return prev; // replies not loaded yet (View replies will fetch) or already have it
            }
            return { ...prev, [parentId]: [incoming, ...list] };
          });
        } else {
          // Top-level: prepend, deduped by id. If this is the echo of OUR OWN just-posted comment, drop the
          // matching pending draft in the SAME update so we never show the "sending" row and the real comment
          // together (both setState calls batch → one render). onSent then no-ops (pending already gone).
          const match =
            incoming.commenterKind === "user"
              ? outboxPendingRef.current.find(
                  (p) => p.commentText === incoming.commentText && (p.visibility ?? "guest") === (incoming.visibility ?? "guest")
                )
              : undefined;
          if (match) {
            // OUR OWN just-posted comment. The SSE frame carries no per-viewer flags and often arrives BEFORE
            // the post response, so stamp mine/editable here (ours + newest → editable) — otherwise this echo
            // wins the id-dedup over onSent's editable copy and the comment stays non-editable until reload.
            const own = { ...incoming, mine: true, editable: true };
            setComments((prev) =>
              prev.some((c) => c.id === own.id) ? prev.map((c) => (c.id === own.id ? { ...c, mine: true, editable: true } : c)) : [own, ...prev]
            );
            outboxDiscardRef.current(match.tempId);
          } else {
            // Someone else posted → it's now the newest, so nothing we could edit is the last comment anymore.
            setComments((prev) => {
              if (prev.some((c) => c.id === incoming.id)) {
                return prev;
              }
              const cleared = prev.some((c) => c.editable) ? prev.map((c) => (c.editable ? { ...c, editable: false } : c)) : prev;
              return [incoming, ...cleared];
            });
          }
        }
      }
    };

    // Held by `openTicketedStream`: it re-mints the ticket and reconnects when the old one stops being accepted (a ticket
    // lives 30 minutes; a reconnect with an expired one used to go silently dead until a page reload — owner, 2026-10-07).
    return openTicketedStream({
      base,
      path: "/comments/stream",
      mintTicket: async () => {
        const res = await CommentService.streamTicket(eventId);

        return res.code === OP_RESULT_CODES.OK && Array.isArray(res.data) ? (res.data[0] as { ticket?: string })?.ticket ?? null : null;
      },
      onTicket: (ticket) => {
        ticketRef.current = ticket; // also authorizes host "is typing" pings
      },
      onOpen: (isReconnect) => {
        if (isReconnect) {
          reload(); // reconnected after a drop — refetch (the crew stream doesn't Last-Event-ID replay)
        }
      },
      onFrame,
    });
  }, [opened, eventId, entityKind, entityId, load, senderId]);

  // Expire stale "typing" indicators (no ping within 6s), same as the guest book.
  useEffect(() => {
    if (!opened) {
      return;
    }
    const iv = setInterval(() => {
      setTyping((prev) => {
        const now = Date.now();
        let changed = false;
        const next: Record<string, { name: string; at: number }> = {};
        for (const [who, v] of Object.entries(prev)) {
          if (now - v.at < 6000) {
            next[who] = v;
          } else {
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    }, 2000);

    return () => clearInterval(iv);
  }, [opened]);

  // ⌘/Ctrl-R reloads the thread while the drawer is open (overrides the browser reload). Uses the current
  // channel; no-op until it's resolved.
  useEffect(() => {
    if (!opened) {
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "r" || e.key === "R")) {
        if (channelIdRef.current) {
          e.preventDefault();
          void load(channelIdRef.current, false);
        }
      }
    };
    window.addEventListener("keydown", onKey);

    return () => window.removeEventListener("keydown", onKey);
  }, [opened, load]);

  // Signal "host is typing" (throttled, fire-and-forget) so GUESTS (and other crew) see it live — the stream
  // ticket authorizes it. When composing a CREW-ONLY comment we mark it crew so guests don't see that
  // presence. Best-effort: needs a ticket (minted by the stream effect above) + a stream base.
  const notifyTyping = useCallback(
    (visibility: "guest" | "crew") => {
      const ticket = ticketRef.current;
      const base = (appEnv.rootApiUrl || "").replace(/\/$/, "");
      if (!ticket || !base || typeof window === "undefined") {
        return;
      }
      const now = Date.now();
      if (now - lastTypingSentRef.current < 3000) {
        return;
      }
      lastTypingSentRef.current = now;

      const q = new URLSearchParams({
        type: "typing",
        ticket,
        entityKind,
        name: hostName,
        senderId,
        visibility,
      });
      if (entityId) {
        q.set("entityId", entityId);
      }
      const url = `${base}/comments/stream?${q.toString()}`;
      try {
        if (navigator.sendBeacon) {
          navigator.sendBeacon(url);
        } else {
          void fetch(url, { method: "POST", keepalive: true });
        }
      } catch {
        // ignore — typing pings are best-effort
      }
    },
    [entityKind, entityId, hostName, senderId]
  );

  // Optimistic outbox — a post appears instantly as "sending", survives reloads, and offers Resend on
  // failure. Scoped per entity thread + this host (localStorage), so drafts never mix or get lost.
  const outbox = useCommentOutbox({
    storageKey: `em_cmt_outbox_admin::${eventId}::${entityKind}::${entityId ?? "root"}`,
    // ⚠ The outbox only keeps a status ("failed") — it throws the REASON away, so a refused
    // post used to offer Resend forever without ever saying why. Keep the result here.
    send: async (draft) => {
      const result = await CommentService.post({
        eventId,
        entityKind,
        entityId,
        commentText: draft.commentText,
        commenterName: hostName,
        visibility: draft.visibility === "crew" ? "crew" : "guest",
      });

      setPostResult(result.didSucceed() ? null : result);

      return result;
    },
    onSent: (res) => {
      const created = (Array.isArray((res as OpResult).data) ? ((res as OpResult).data as EventComment[]) : []).filter((c) => !!c.id);
      setComments((prev) => {
        const known = new Set(prev.map((c) => c.id));
        const fresh = created.filter((c) => !known.has(c.id));
        return fresh.length ? [...fresh, ...prev] : prev;
      });
    },
  });

  // Keep the stream effect's bridge refs current (it reconciles the SSE echo of our own post against these).
  outboxPendingRef.current = outbox.pending;
  outboxDiscardRef.current = outbox.discard;

  // Auto-scroll the thread to the newest comment (bottom) on open/load and when a new comment or pending draft
  // arrives — but NOT when "Show previous" prepends OLDER ones (comments are stored newest-first, so the newest
  // `comments[0]` is unchanged then). Chat-style: you always land on the latest.
  const newestId = comments[0]?.id;
  useEffect(() => {
    if (!opened) {
      return;
    }
    requestAnimationFrame(() => bottomRef.current?.scrollIntoView({ block: "end" }));
  }, [opened, newestId, outbox.pending.length]);

  const post = useCallback(() => {
    const body = text.trim();
    if (!body) {
      return;
    }
    // `/help` (and aliases) opens the commands panel instead of posting a "/help" comment.
    if (isHelpCommand(body)) {
      setText("");
      setHelpOpen(true);
      return;
    }
    setError(null);
    setPostResult(null);
    outbox.submit({ entityKind, entityId, commentText: body, commenterName: hostName, visibility: crewOnly ? "crew" : "guest" });
    setText(""); // clear immediately so you can keep typing
  }, [text, entityKind, entityId, hostName, crewOnly, outbox]);

  const loadReplies = useCallback(
    async (parentId: string) => {
      // The replies of ONE comment, read whole — a cap here silently dropped the rest (ROADMAP #118).
      const res = await listAllPages(CommentService.list, { eventId: [eventId], channelCommentId: parentId });
      if (res.code === OP_RESULT_CODES.OK) {
        setRepliesByParent((prev) => ({
          ...prev,
          [parentId]: (Array.isArray(res.data) ? (res.data as EventComment[]) : []).filter((c) => !!c.id),
        }));
      }
    },
    [eventId]
  );

  const toggleReplies = useCallback(
    async (parentId: string) => {
      const willOpen = !openReplies[parentId];
      setOpenReplies((prev) => ({ ...prev, [parentId]: willOpen }));
      if (willOpen && !repliesByParent[parentId]) {
        await loadReplies(parentId);
      }
    },
    [openReplies, repliesByParent, loadReplies]
  );

  const postReply = useCallback(
    async (parentId: string) => {
      const body = replyText.trim();
      if (!body) {
        return;
      }
      setReplyPosting(true);
      setError(null);
      setReplyResult(null);
      const res = await CommentService.post({
        eventId,
        entityKind,
        entityId,
        commentText: body,
        channelCommentId: parentId,
        commenterName: hostName,
        visibility: replyCrewOnly ? "crew" : "guest",
      });
      setReplyPosting(false);

      if (res.code === OP_RESULT_CODES.OK) {
        setReplyText("");
        setReplyFor(null);
        setReplyCrewOnly(false);
        setOpenReplies((prev) => ({ ...prev, [parentId]: true }));
        await loadReplies(parentId);
        // Refresh the hasReplies flags — only if the drawer still shows this thread: a reload is the newest read, so a
        // reload of the thread the reply was posted in would replace the one opened since (#119 review).
        if (channelId && channelId === channelIdRef.current) {
          await load(channelId, false);
        }
      } else {
        // ⚠ This used to set the thread-level `error`, which renders under the MAIN
        // composer at the bottom — a reply box mid-thread failed with its explanation
        // somewhere else entirely. It stays on the reply box now.
        setReplyResult(res);
      }
    },
    [replyText, eventId, entityKind, entityId, hostName, replyCrewOnly, loadReplies, channelId, load]
  );

  // Save an inline edit of one's own comment. The server re-checks ownership + that it's still the newest
  // comment in the thread; on success we patch the text live (others get it via the comment.updated broadcast).
  const saveEdit = useCallback(
    async (c: EventComment) => {
      const body = editText.trim();
      if (!body) {
        return;
      }
      if (body === (c.commentText || "")) {
        setEditFor(null);
        return;
      }

      setEditPosting(true);
      setEditResult(null);
      const res = await CommentService.edit({ eventId, channelCommentId: c.id, commentText: body });
      setEditPosting(false);

      if (res.code === OP_RESULT_CODES.OK) {
        const patch = (x: EventComment): EventComment => (x.id === c.id ? { ...x, commentText: body } : x);
        setComments((prev) => prev.map(patch));
        setRepliesByParent((prev) => {
          const next: Record<string, EventComment[]> = {};
          for (const [pid, list] of Object.entries(prev)) {
            next[pid] = list.map(patch);
          }
          return next;
        });
        setEditFor(null);
      } else {
        setEditResult(res);
      }
    },
    [editText, eventId]
  );

  const confirmRemove = useCallback(async () => {
    if (!removeId) {
      return;
    }
    setRemoving(true);
    const res = await CommentService.remove(removeId);
    setRemoving(false);
    if (res.code === OP_RESULT_CODES.OK) {
      const id = removeId;
      setComments((prev) => prev.filter((c) => c.id !== id));
      setRepliesByParent((prev) => {
        const next: Record<string, EventComment[]> = {};
        for (const [k, list] of Object.entries(prev)) {
          next[k] = list.filter((c) => c.id !== id);
        }
        return next;
      });
      setRemoveId(null);
    } else {
      setError(errText(res));
      setRemoveId(null);
    }
  }, [removeId]);

  // Apply a transform to a comment's reaction summary in place (top-level or a loaded reply).
  const updateCommentReactions = useCallback(
    (commentId: string, updater: (reactions: EventCommentReaction[]) => EventCommentReaction[]) => {
      setComments((prev) => prev.map((c) => (c.id === commentId ? { ...c, reactions: updater(c.reactions || []) } : c)));
      setRepliesByParent((prev) => {
        let changed = false;
        const next: Record<string, EventComment[]> = {};
        for (const [pid, list] of Object.entries(prev)) {
          next[pid] = list.map((c) => {
            if (c.id === commentId) {
              changed = true;
              return { ...c, reactions: updater(c.reactions || []) };
            }
            return c;
          });
        }
        return changed ? next : prev;
      });
    },
    []
  );
  updateReactionsRef.current = updateCommentReactions;

  // ---- Error surfacing ---------------------------------------------------------------------
  // Three independent composers, three OpResults. Each one's `commentText` refusal is drawn on
  // ITS OWN textarea; its unattached errors sit next to that box (the reply/edit boxes live
  // mid-thread, so the footer would be a whole scroll away from them). Only the main composer —
  // which sits directly above the footer — and the thread-level failures use the drawer alert.
  const { fields: composerFieldErrors, genericErrors: composerGenericErrors } = useMemo(
    () => getFormErrors(postResult, COMMENT_FORM_FIELDS),
    [postResult]
  );
  const { fields: replyFieldErrors, genericErrors: replyGenericErrors } = useMemo(
    () => getFormErrors(replyResult, COMMENT_FORM_FIELDS),
    [replyResult]
  );
  const { fields: editFieldErrors, genericErrors: editGenericErrors } = useMemo(
    () => getFormErrors(editResult, COMMENT_FORM_FIELDS),
    [editResult]
  );

  const replyGenericError = formatErrors(replyGenericErrors);
  const editGenericError = formatErrors(editGenericErrors);
  const drawerErrors = useMemo(
    () => formatErrors([...(error ? [error] : []), ...composerGenericErrors]),
    [error, composerGenericErrors]
  );
  // ⚠ `.some(Boolean)`, never `Object.keys(...).length` — getFormErrors pre-fills every known
  // name with undefined, so a keys check would flag a clean thread.
  const hasFieldErrors = [composerFieldErrors, replyFieldErrors, editFieldErrors].some((f) =>
    Object.values(f).some(Boolean)
  );

  // Toggle a reaction as host/crew — optimistic (instant), then reconcile with the authoritative summary
  // (or revert on failure). The server broadcasts reaction.updated so guests + other crew update live.
  const react = useCallback(
    async (commentId: string, emoji: string) => {
      setError(null);
      updateCommentReactions(commentId, (rs) => toggleReactionSummary(rs, emoji));

      const res = await CommentService.react({ eventId, channelCommentId: commentId, reaction: emoji });

      if (res.code === OP_RESULT_CODES.OK && Array.isArray(res.data) && res.data[0]) {
        updateCommentReactions(commentId, () => ((res.data[0] as EventComment).reactions as EventCommentReaction[]) || []);
      } else {
        updateCommentReactions(commentId, (rs) => toggleReactionSummary(rs, emoji)); // revert
        if (res.code !== OP_RESULT_CODES.OK) {
          setError(errText(res));
        }
      }
    },
    [eventId, updateCommentReactions]
  );

  // Existing reactions as tappable pills (md-sized text) — the "reactions row" of the footer. Null when empty.
  const renderReactionChips = (c: EventComment) => {
    const summary = (c.reactions || []).filter((r) => (r.count || 0) > 0);
    if (summary.length === 0) {
      return null;
    }
    return (
      <Group gap={6} mt={8} wrap="wrap" align="center">
        {summary.map((r) => (
          <Button
            key={r.reaction}
            variant={r.mine ? "light" : "default"}
            color={r.mine ? "blue" : "gray"}
            size="compact-md"
            radius="xl"
            onClick={() => void react(c.id, r.reaction)}
            styles={{ label: { display: "flex", alignItems: "center", gap: 5 } }}
          >
            <span style={{ fontSize: 16, lineHeight: 1 }}>{r.reaction}</span>
            <span>{r.count}</span>
          </Button>
        ))}
      </Group>
    );
  };

  // The "add a reaction" control for the action bar — an emoji picker.
  const renderReactMenu = (c: EventComment) => {
    const byEmoji = new Map((c.reactions || []).map((r) => [r.reaction, r]));
    return (
      <ReactionMenu
        target={
          <Tooltip label="Add a reaction" withArrow>
            <ActionIcon variant="subtle" color="gray" size="lg" radius="xl" aria-label="Add a reaction">
              <AppIcon iconName="MoodSmile" size={20} />
            </ActionIcon>
          </Tooltip>
        }
        isMine={(emoji) => !!byEmoji.get(emoji)?.mine}
        onPick={(emoji) => void react(c.id, emoji)}
      />
    );
  };

  // The single action row of the footer — react · reply · replies · edit · remove, on ONE line with large,
  // easy-to-tap icon targets. Reply/replies are top-level only; edit shows while the comment is still the
  // newest (author-only, server-enforced). Host can always remove (moderation).
  const renderActionBar = (c: EventComment, isReply: boolean) => {
    const canViewReplies = !isReply && !!c.hasReplies;
    const canEdit = !!c.editable && !c.hasReplies;

    return (
      <Group gap={4} mt={8} wrap="wrap" align="center">
        {renderReactMenu(c)}

        {!isReply && (
          <Tooltip label="Reply" withArrow>
            <ActionIcon
              variant="subtle"
              color="gray"
              size="lg"
              radius="xl"
              aria-label="Reply"
              onClick={() => {
                setReplyFor(replyFor === c.id ? null : c.id);
                setReplyText("");
                setReplyCrewOnly(c.visibility === "crew");
                // One reply box at a time, one result — clear it so the NEXT comment's box
                // doesn't open showing the previous one's refusal.
                setReplyResult(null);
              }}
            >
              <AppIcon iconName="ArrowBackUp" size={20} />
            </ActionIcon>
          </Tooltip>
        )}

        {canViewReplies && (
          <Tooltip label={openReplies[c.id] ? "Hide replies" : "View replies"} withArrow>
            <ActionIcon
              variant={openReplies[c.id] ? "light" : "subtle"}
              color={openReplies[c.id] ? "blue" : "gray"}
              size="lg"
              radius="xl"
              aria-label="View replies"
              onClick={() => void toggleReplies(c.id)}
            >
              <AppIcon iconName="MessageCircle" size={20} />
            </ActionIcon>
          </Tooltip>
        )}

        {canEdit && (
          <Tooltip label="Edit" withArrow>
            <ActionIcon
              variant="subtle"
              color="gray"
              size="lg"
              radius="xl"
              aria-label="Edit"
              onClick={() => {
                setEditFor(c.id);
                setEditText(c.commentText || "");
                setEditOriginal(c.commentText || "");
                setEditResult(null);
              }}
            >
              <AppIcon iconName="Edit" size={20} />
            </ActionIcon>
          </Tooltip>
        )}

        <Tooltip label="Remove" withArrow>
          <ActionIcon variant="subtle" color="red" size="lg" radius="xl" aria-label="Remove" onClick={() => setRemoveId(c.id)}>
            <AppIcon iconName="Trash" size={20} />
          </ActionIcon>
        </Tooltip>
      </Group>
    );
  };

  // An unsent (optimistic) host/crew comment from the outbox — shown at the top of the thread.
  const renderPending = (p: OutboxComment) => (
    <Paper
      key={p.tempId}
      withBorder
      radius="md"
      p="sm"
      style={{ opacity: p.status === "sending" ? 0.7 : 1, borderColor: p.status === "failed" ? "var(--mantine-color-red-4)" : undefined }}
    >
      <Group gap={8} mb={4} wrap="wrap" align="center">
        <Text fw={600} size="md">
          {p.commenterName || "You"}
        </Text>
        {p.visibility === "crew" ? (
          <Badge size="xs" variant="light" color="orange" leftSection={<AppIcon iconName="Lock" size={10} />}>
            Crew only
          </Badge>
        ) : null}
        {p.status === "sending" ? (
          <Group gap={4} wrap="nowrap">
            <Loader size={12} />
            <Text size="xs" c="dimmed">
              Sending…
            </Text>
          </Group>
        ) : (
          <Text size="xs" c="red">
            Couldn&rsquo;t send
          </Text>
        )}
      </Group>
      <Text size="md" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
        {p.commentText}
      </Text>
      {p.status === "failed" ? (
        <Group gap="xs" mt={6}>
          <Button variant="light" color="red" size="compact-md" onClick={() => outbox.resend(p.tempId)}>
            Resend
          </Button>
          <Button variant="subtle" color="gray" size="compact-md" onClick={() => outbox.discard(p.tempId)}>
            Discard
          </Button>
        </Group>
      ) : null}
    </Paper>
  );

  const renderComment = (c: EventComment, isReply = false) => (
    <Paper key={c.id} withBorder radius="md" p="sm" ml={isReply ? "lg" : 0}>
      <Group gap={8} mb={4} wrap="wrap" align="center">
        {/* A2e #91 — the author's picture (a guest's photo or Gravatar, a host's account picture), else initials. */}
        <Avatar size={32} radius="xl" src={commentAuthorPicture(c)} name={c.commenterName || "Guest"} color="initials" alt="" />
        <Text fw={600} size="md">
          {c.commenterName || "Guest"}
        </Text>
        {c.commenterKind === "user" ? (
          <Badge size="xs" variant="light" color="blue">
            Host
          </Badge>
        ) : null}
        {c.visibility === "crew" ? (
          <Badge size="xs" variant="light" color="orange" leftSection={<AppIcon iconName="Lock" size={10} />}>
            Crew only
          </Badge>
        ) : null}
        <Text size="xs" c="dimmed">
          {fmtWhen(c.createdAt)}
        </Text>
      </Group>
      {editFor === c.id ? (
        <Stack gap="xs">
          <Textarea
            value={editText}
            onChange={(e) => setEditText(e.currentTarget.value)}
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                e.preventDefault();
                if (editText.trim()) {
                  void saveEdit(c);
                }
              }
            }}
            autosize
            minRows={2}
            autoFocus
            error={editFieldErrors.commentText}
          />
          {editGenericError ? (
            <Text size="xs" c="red">
              {editGenericError}
            </Text>
          ) : null}
          <Group gap="xs" justify="flex-end">
            <Button variant="default" onClick={() => setEditFor(null)}>
              Cancel
            </Button>
            <Button loading={editPosting} disabled={!editText.trim()} onClick={() => void saveEdit(c)}>
              Save
            </Button>
          </Group>
        </Stack>
      ) : (
        <CommentBody text={c.commentText} />
      )}

      {/* Footer — reactions row + a single action row (react · reply · replies · edit · remove). Hidden while editing. */}
      {editFor !== c.id ? (
        <>
          {renderReactionChips(c)}
          {renderActionBar(c, isReply)}
        </>
      ) : null}

      {!isReply ? (
        <>
          <Collapse in={!!openReplies[c.id]}>
            <Stack gap="xs" mt="xs">
              {[...(repliesByParent[c.id] || [])].reverse().map((r) => renderComment(r, true))}
            </Stack>
          </Collapse>

          {replyFor === c.id ? (
            <Stack gap="xs" mt="xs">
              <Textarea
                value={replyText}
                onChange={(e) => {
                  setReplyText(e.currentTarget.value);
                  notifyTyping(replyCrewOnly ? "crew" : "guest");
                }}
                onKeyDown={(e) => {
                  if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                    e.preventDefault();
                    if (replyText.trim()) {
                      void postReply(c.id);
                    }
                  }
                }}
                placeholder="Write a reply…"
                autosize
                minRows={2}
                error={replyFieldErrors.commentText}
              />
              {replyGenericError ? (
                <Text size="xs" c="red">
                  {replyGenericError}
                </Text>
              ) : null}
              <Group justify="space-between" align="center" wrap="wrap" gap="sm">
                <Switch
                  label="Crew only"
                  checked={replyCrewOnly}
                  onChange={(e) => setReplyCrewOnly(e.currentTarget.checked)}
                />
                <Group gap="xs">
                  <Button variant="default" onClick={() => setReplyFor(null)}>
                    Cancel
                  </Button>
                  <Button loading={replyPosting} disabled={!replyText.trim()} onClick={() => void postReply(c.id)}>
                    Reply
                  </Button>
                </Group>
              </Group>
            </Stack>
          ) : null}
        </>
      ) : null}
    </Paper>
  );

  const typingNames = Object.values(typing).map((v) => v.name);
  const typingText =
    typingNames.length === 0
      ? ""
      : typingNames.length === 1
        ? `${typingNames[0]} is typing…`
        : typingNames.length === 2
          ? `${typingNames[0]} and ${typingNames[1]} are typing…`
          : `${typingNames[0]}, ${typingNames[1]} and ${typingNames.length - 2} more are typing…`;

  return (
    <>
      <AppDrawer
        opened={opened}
        onClose={onClose}
        // The drawer unmounts on close, so an unsent comment, reply or edit would be lost: Esc / a click outside / the
        // X ask first (#123).
        isDirty={!!text.trim() || (!!replyFor && !!replyText.trim()) || (!!editFor && editText.trim() !== editOriginal.trim())}
        title={title ? `Comments · ${title}` : "Comments"}
        size="md"
        position="right"
        errors={drawerErrors}
        hasFieldErrors={hasFieldErrors}
        actionType="custom"
        customActions={
          // The composer IS the footer (owner, 2026-10-23): always on screen however long the thread, and the drawer
          // body is the ONE scroll area — a thread scrolling inside a scrolling body left two scrollbars.
          <Stack gap="sm">
            {typingText ? (
              <Text size="md" c="dimmed" fs="italic" aria-live="polite">
                {typingText}
              </Text>
            ) : null}
            <Textarea
              ref={composerRef}
              value={text}
              onChange={(e) => {
                setText(e.currentTarget.value);
                notifyTyping(crewOnly ? "crew" : "guest");
              }}
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                  e.preventDefault();
                  post();
                }
              }}
              placeholder="Add a comment as host/crew…"
              autosize
              minRows={2}
              // A long draft scrolls inside the box rather than pushing the thread off the screen.
              maxRows={6}
              error={composerFieldErrors.commentText}
            />
            <Group justify="space-between" align="center" wrap="wrap" gap="sm">
              <Group gap="md" align="center" wrap="wrap">
                <Switch
                  label="Crew only"
                  description="Host & crew see it; guests don't."
                  checked={crewOnly}
                  onChange={(e) => setCrewOnly(e.currentTarget.checked)}
                />
                <CommentComposerHelp opened={helpOpen} onOpenedChange={setHelpOpen} onPick={insertIntoComposer} />
              </Group>
              {/* Close goes through the drawer's discard question — an unsent comment is asked about, not dropped. */}
              <DiscardGuard>
                {(leave) => (
                  <Group gap="sm" wrap="nowrap" ml="auto">
                    <Button variant="default" size="lg" onClick={() => leave()}>
                      Close
                    </Button>
                    <Tooltip label="⌘↵" withArrow position="top" openDelay={400}>
                      <Button size="lg" disabled={!text.trim()} onClick={() => post()} leftSection={<AppIcon iconName="Send" size={18} />}>
                        Post
                      </Button>
                    </Tooltip>
                  </Group>
                )}
              </DiscardGuard>
            </Group>
          </Stack>
        }
      >
        {/* Thread — chat-style: oldest at the top, newest at the bottom, right above the composer. "Show previous"
            (at the top) loads older comments; the drawer body auto-scrolls to the newest. */}
        {!isLoaded && comments.length === 0 && outbox.pending.length === 0 ? (
          <Group justify="center" py="md">
            <Loader size="sm" />
          </Group>
        ) : comments.length === 0 && outbox.pending.length === 0 ? (
          <Text c="dimmed" ta="center" py="md">
            No comments yet.
          </Text>
        ) : (
          <Stack gap="sm" px={2}>
            {hasMore && channelId ? (
              <LoadMoreButton hasMore={hasMore} loading={loading} loadingMore={loadingMore} onLoadMore={loadMore} label="Show previous" />
            ) : null}
            {[...comments].reverse().map((c) => renderComment(c))}
            {outbox.pending.map(renderPending)}
            <div ref={bottomRef} />
          </Stack>
        )}
      </AppDrawer>

      <ConfirmModal
        opened={!!removeId}
        onClose={() => setRemoveId(null)}
        onConfirm={() => void confirmRemove()}
        confirmInProgress={removing}
        title="Remove comment"
        message="Remove this comment? This can't be undone."
        confirmLabel="Remove"
        type="danger"
      />
    </>
  );
}
